import type { PillarKey } from './navPillars';

// Moved out of Sidebar.tsx (a component file — a non-component export there trips
// react-refresh/only-export-components) so this data can also be imported by the SF-128
// REALM_ITEMS/Province-section parity test (constants/__tests__/realmNav.test.ts) without
// pulling in the Sidebar component itself.

export interface NavItemData {
  route: string;
  label: string;
  iconPath: string;
  pillar: PillarKey;
  disabled?: boolean;
  badge?: number;
}

export interface NavSectionData {
  title: string;
  items: readonly NavItemData[];
}

export const NAV_SECTIONS: readonly NavSectionData[] = [
  {
    title: 'Internal Affairs',
    items: [
      { route: '/province', label: 'Throne',   pillar: 'neutral',  iconPath: 'M4 12H12M5 4H11L12 2H4L5 4ZM6 4V8H10V4M8 8V12' },
      // SF-162: Council's sidebar entry removed — Council.tsx is deleted outright, its remaining
      // content merged into Training.tsx's Overview tab (`/military?tab=overview`).
      { route: '/strategy', label: 'Strategy', pillar: 'neutral',  iconPath: 'M3 3H13V13H3ZM3 8H13M8 3V13' },
    ],
  },
  {
    title: 'Province',
    items: [
      { route: '/buildings', label: 'Buildings', pillar: 'economy',  iconPath: 'M3 14V6L8 2L13 6V14M3 14H13M6 14V10H10V14' },
      { route: '/military',  label: 'Military',  pillar: 'military', iconPath: 'M3 3L13 13M3 3L3 6M3 3L6 3M13 3L3 13M13 3L13 6M13 3L10 3' },
      { route: '/explore',   label: 'Explore',   pillar: 'neutral',  iconPath: 'M2 8A6 6 0 1012 2M2 8L6 4M2 8L6 12' },
      { route: '/sciences',  label: 'Sciences',  pillar: 'science',  iconPath: 'M6 2V6L3 12H13L10 6V2M5 2H11' },
      { route: '/economy',   label: 'Economy',   pillar: 'economy',  iconPath: 'M8 2V13M4 4H12M4 4L2.5 8H5.5L4 4ZM12 4L10.5 8H13.5L12 4Z' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { route: '/attack',   label: 'War Room',  pillar: 'military', iconPath: 'M2 3H14L13 8H3ZM4 8V13H12V8M8 3V8' },
      { route: '/mystics',  label: 'Mystics',   pillar: 'military', iconPath: 'M8 2L10 6L14 7L11 10L12 14L8 12L4 14L5 10L2 7L6 6Z' },
      { route: '/thievery', label: 'Thievery',  pillar: 'military', iconPath: 'M8 3A3 3 0 006 6V8H10V6A3 3 0 008 3ZM5 8H11V13H5Z' },
    ],
  },
  {
    title: 'Kingdom',
    items: [
      { route: '/kingdom/roster',  label: 'My Kingdom', pillar: 'neutral', iconPath: 'M3 5H13M8 5V14M4 8H12M5 11H11' },
      { route: '/kingdom/politics', label: 'Politics', pillar: 'neutral', iconPath: 'M8 2L2 6H14ZM4 7V12H6V7M10 7V12H12V7M3 12H13V14H3Z' },
      { route: '/kingdom/dragons', label: 'Dragons',  pillar: 'neutral', iconPath: 'M4 8C4 4 8 2 8 2S12 4 12 8L10 10L12 13H4L6 10ZM7 6H9' },
      { route: '/kingdom/aid',     label: 'Aid',      pillar: 'neutral', iconPath: 'M8 4V12M4 8H12' },
    ],
  },
  {
    title: 'World',
    items: [
      // SF-169: repointed from /world/dossier (which now has its own distinct "Dossier" entry
      // below) to /world/kingdoms — the new live, public, browsable-by-anyone World Viewer.
      { route: '/world/kingdoms', label: 'Kingdoms',  pillar: 'neutral',  iconPath: 'M3 5H13M8 5V14M4 8H12M5 11H11' },
      { route: '/world/dossier', label: 'Dossier',    pillar: 'neutral',  iconPath: 'M4 3H12V13L8 11L4 13ZM6 6H10M6 8H10' },
      // SF-171: new entry, added alongside the existing ones below (not a repoint) — both the
      // new and existing World-section destinations keep their own reachable nav path.
      { route: '/world/targets', label: 'Target Finder', pillar: 'neutral', iconPath: 'M8 1V4M8 12V15M1 8H4M12 8H15' },
      // SF-171: renamed from "Intelligence" — now accurately single-purpose (Target Finder split
      // out to its own entry above), and this file is already being touched.
      { route: '/intel',       label: 'Intel Hub',    pillar: 'military', iconPath: 'M8 2C4.5 2 2 5 2 8H14C14 5 11.5 2 8 2ZM4 10L3 14M12 10L13 14M6 10V14M10 10V14' },
      { route: '/recent-news', label: 'Recent News',  pillar: 'neutral',  iconPath: 'M2 4H14V12H2ZM5 7H11M5 9H9' },
      { route: '/relations', label: 'Relations', pillar: 'neutral', iconPath: 'M4 4A2 2 0 108 4A2 2 0 104 4ZM2 10C2 8 4 7 6 7M10 7C12 7 14 8 14 10M10 4A2 2 0 1014 4' },
      // SF-159: last remaining disabled World entry — flipped to enabled now that /rankings has
      // a real page.
      { route: '/rankings', label: 'Rankings',  pillar: 'neutral',  iconPath: 'M3 14V8H6V14M7 14V4H10V14M11 14V6H14V14' },
    ],
  },
];
